/**
 * Grand Quack Auto — end-of-run highlights reel.
 *
 * Shown on (a) all objectives complete, (b) the death screen's VIEW STATS
 * button (which is the manual end-run path), and indirectly on (c) ABSOLUTE
 * QUACKERY death — that path goes ABSOLUTE QUACKERY → death screen → click
 * VIEW STATS → here. Phase B3 of the virality plan.
 *
 * The card is the screenshot magnet: tier emoji chain across the top,
 * top combo banner with its name, six counters in a grid, and a fat row
 * of share buttons. "Play again" reloads.
 */

import { getMaxCombo, getMaxComboName } from '../systems/comboSystem';
import {
  DUCK_TIERS, getCurrentTierIndex, getCurrentTierName,
} from '../systems/duckEvolutionSystem';
import { getTotalHatched, getTotalKilled } from '../systems/ducklingSystem';
import { getTotalScared } from '../systems/npcSystem';
import { getTotalHijacked } from '../systems/vehicleSystem';
import type { World } from '../types';

let shown = false;
const GAME_URL = 'https://grand-quack-auto.pages.dev';

/** Show the stats overlay (called automatically on objective complete). */
export function showStatsScreen(world: World): void {
  if (shown) return;
  if (document.getElementById('death-screen')) return;
  renderStatsScreen(world);
}

/**
 * Show the stats overlay regardless of state. Used by the death screen's
 * VIEW STATS button so the player can pull up the highlights reel even
 * after a non-objective death.
 */
export function forceShowStatsScreen(world: World): void {
  document.getElementById('stats-screen')?.remove();
  renderStatsScreen(world);
}

function renderStatsScreen(world: World): void {
  shown = true;

  const tier = getCurrentTierIndex();
  const tierName = getCurrentTierName();
  const stars = '\u2605'.repeat(Math.min(tier, 3));
  const chaos = Math.floor(world.chaos.points);
  const scared = getTotalScared();
  const hijacked = getTotalHijacked();
  const hatched = getTotalHatched();
  const killed = getTotalKilled();
  const maxCombo = getMaxCombo();
  const maxComboName = getMaxComboName();
  const time = formatTime(world.time.elapsed);

  // Build the duck-evolution emoji chain — every tier the player passed
  // through, joined by →. Visually conveys progression without numbers.
  const evoChain = DUCK_TIERS.slice(0, tier + 1).map(t => t.icon).join(' → ');

  // Top-combo banner — only show if the player ever broke x2.
  const comboLine = maxCombo >= 2
    ? `<div class="stats-combo-banner">TOP COMBO: <b>x${maxCombo} ${maxComboName}</b></div>`
    : '';

  const shareText = encodeURIComponent(buildShareText({
    stars, tierName, chaos, scared, hatched, killed, hijacked, time,
    maxCombo, maxComboName,
  }));
  const shareUrl = encodeURIComponent(GAME_URL);

  const overlay = document.createElement('div');
  overlay.id = 'stats-screen';
  overlay.innerHTML = `
    <div class="stats-card">
      <h1>GRAND QUACK AUTO</h1>
      <h2>RUN COMPLETE</h2>
      <div class="stats-evo-chain">${evoChain}</div>
      <h3>${stars} ${tierName.toUpperCase()} ${stars}</h3>
      ${comboLine}
      <div class="stats-grid">
        <div class="stat"><span class="stat-val">${chaos}</span><span class="stat-label">CHAOS</span></div>
        <div class="stat"><span class="stat-val">${scared}</span><span class="stat-label">SCARED</span></div>
        <div class="stat"><span class="stat-val">${time}</span><span class="stat-label">TIME</span></div>
        <div class="stat"><span class="stat-val">${tier}/${DUCK_TIERS.length - 1}</span><span class="stat-label">EVOLUTION</span></div>
        <div class="stat"><span class="stat-val">${hatched}</span><span class="stat-label">🐥 HATCHED</span></div>
        <div class="stat"><span class="stat-val">${killed}</span><span class="stat-label">💔 LOST</span></div>
        <div class="stat"><span class="stat-val">${hijacked}</span><span class="stat-label">🚗 HIJACKED</span></div>
        <div class="stat"><span class="stat-val">${maxCombo > 0 ? 'x' + maxCombo : '—'}</span><span class="stat-label">MAX COMBO</span></div>
      </div>
      <div class="stats-share-label">📸 SHARE MY RUN</div>
      <div class="stats-share-row">
        <a class="stats-share-btn stats-tw" href="https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}" target="_blank">𝕏 Tweet</a>
        <a class="stats-share-btn stats-fb" href="https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}" target="_blank">f Share</a>
        <a class="stats-share-btn stats-rd" href="https://www.reddit.com/submit?url=${shareUrl}&title=${shareText}" target="_blank">r Post</a>
        <button class="stats-share-btn stats-cp" id="stats-copy">📋 Copy</button>
        <button class="stats-share-btn stats-ss" id="stats-screenshot">📸 Save</button>
      </div>
      <div class="stats-actions">
        <button id="stats-play-again">🔄 PLAY AGAIN</button>
        <button id="stats-next-game">NEXT GAME ➡️</button>
      </div>
      <p class="stats-footer">Built with Claude Code (Opus 4.6) for #VibeJam2026</p>
    </div>
  `;
  document.body.appendChild(overlay);

  document.getElementById('stats-play-again')?.addEventListener('click', () => {
    window.location.href = window.location.origin + window.location.pathname;
  });

  document.getElementById('stats-next-game')?.addEventListener('click', () => {
    const ref = encodeURIComponent(window.location.origin + window.location.pathname);
    window.location.href = `https://vibej.am/portal/2026?ref=${ref}&username=QuackDuck&color=FFD700`;
  });

  document.getElementById('stats-copy')?.addEventListener('click', () => {
    const text = decodeURIComponent(shareText) + '\n' + GAME_URL;
    navigator.clipboard?.writeText(text);
    const btn = document.getElementById('stats-copy');
    if (btn) { btn.textContent = '✅ Copied!'; setTimeout(() => { btn.textContent = '📋 Copy'; }, 2000); }
  });

  document.getElementById('stats-screenshot')?.addEventListener('click', () => {
    world.renderer.render(world.scene, world.camera);
    const dataUrl = world.renderer.domElement.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'grand-quack-auto-run.png';
    a.click();
  });
}

interface ShareCtx {
  stars: string; tierName: string; chaos: number; scared: number;
  hatched: number; killed: number; hijacked: number; time: string;
  maxCombo: number; maxComboName: string;
}

function buildShareText(c: ShareCtx): string {
  const comboLine = c.maxCombo >= 2
    ? `\n🔥 Top combo: x${c.maxCombo} ${c.maxComboName}`
    : '';
  return (
    `🦆 My Grand Quack Auto run:\n` +
    `${c.stars} ${c.tierName.toUpperCase()}\n` +
    `${c.chaos} chaos · ${c.scared} scared · ${c.time}` +
    comboLine + '\n' +
    `🐥 ${c.hatched} hatched · 💔 ${c.killed} lost · 🚗 ${c.hijacked} hijacked\n\n` +
    `Beat my duck: #GrandQuackAuto #VibeJam2026`
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
